/**
 * [Phase 2b-B §4.1] CommissionerSetup - new wizard shell.
 *
 * REPLACES the previous 5-step linear wizard wholesale. The previous shell's
 * payload (`{name, settings}`) shape is preserved; root-level fields like
 * `draft_date` and `commissioner_email` were never read by any backend
 * consumer (R7 grep verified) so removing them is a no-op for the API.
 *
 * Architecture (§4.1, R4):
 *   • Sidebar nav with 8 steps. Linear-to-free state machine: a step
 *     unlocks once it has been visited; thereafter any visited step can be
 *     jumped to via the sidebar.
 *   • Pre-launch: form state is persisted to localStorage (R4 "Save").
 *     "Launch League" button at the bottom POSTs the full payload
 *     atomically per §3.4.
 *   • Post-launch: bottom button label flips to "Save Changes"
 *     (PATCHes via existing PUT /api/leagues/{id}/settings).
 *
 * 2b-B scope: Steps 1, 2 (basic), 4 are functional. Steps 3, 5, 6, 7, 8 are
 * sidebar-visible placeholders that ship in 2b-C / 2b-D.
 */
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Check, Rocket, Save } from "lucide-react";
import { useAuth, authHeaders } from "../contexts/AuthContext";

import Step1LeagueType, {
  computeLeagueType,
  validateStep1,
} from "../components/wizard/Step1LeagueType";
import Step2Roster, { validateStep2 } from "../components/wizard/Step2Roster";
import Step3Scoring, {
  validateStep3,
  pruneToOverrides,
  detectPreset,
} from "../components/wizard/Step3Scoring";
import Step4DraftSettings, {
  validateStep4,
} from "../components/wizard/Step4DraftSettings";
import Step5SeasonTicketHolders from "../components/wizard/Step5SeasonTicketHolders";
import Step6Keepers from "../components/wizard/Step6Keepers";
import Step7NominationOrder from "../components/wizard/Step7NominationOrder";
import Step8Review from "../components/wizard/Step8Review";
import CopySettingsModal, { parseCopiedSettings } from "../components/wizard/CopySettingsModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "pgf_wizard_draft_v1";

const STEPS = [
  { id: 1, label: "League Type", phase: "2b-B" },
  { id: 2, label: "Roster", phase: "2b-C" },
  { id: 3, label: "Scoring", phase: "2b-C" },
  { id: 4, label: "Draft Settings", phase: "2b-B" },
  { id: 5, label: "Invites (Live)", phase: "2b-D" },
  { id: 6, label: "Keepers (Live)", phase: "2b-D" },
  { id: 7, label: "Nomination Order (Live)", phase: "2b-D" },
  { id: 8, label: "Review & Launch", phase: "2b-D" },
];

const DEFAULT_FORM = {
  // Step 1
  top: "draft", // 'draft' | 'season'
  draftFormat: "auction",
  draftMode: "live", // 'live' | 'mock_season' | 'mock_only'
  seasonMode: "mock", // 'live' (grayed) | 'mock'
  // [Item 1] Season-only: how the season is won. Gates team-count parity.
  seasonLeagueType: "h2h", // 'h2h' | 'points'
  name: "",
  // [Phase 2c-D §6.7 NEW-6] commissioner's own team name (optional)
  commissioner_team_name: "",
  num_teams: 12,
  // [TD-66 B.1] CPU manager count - pre-lobby commissioner decision.
  cpu_count: 0,
  // Step 2
  roster: { qb: 1, rb: 2, wr: 2, te: 1, flex: 1, superflex: 0, k: 1, def: 1, bench: 5 },
  // Step 4 (auction)
  budget: 200,
  bidding_timer_seconds: 30,
  nomination_timer_seconds: 60,
  antisnipe_trigger_seconds: 10,
  antisnipe_extend_seconds: 10,
  // [Phase 2c-D §6.1] Step 4 (snake-only)
  pick_timer_enabled: true,
  pick_timer_seconds: 60,
  allow_pick_resubmit: true,
  // Step 3 (Phase 2b-C)
  scoring_rules: {}, // empty = use preset (half_ppr)
  // Phase 2b later steps (collected for atomic POST; default values until 2b-D)
  use_keepers: false,
  nomination_order_mode: "lotto",
};

async function waitForToken(maxMs = 3000, intervalMs = 150) {
  const start = Date.now();
  return new Promise((resolve) => {
    const check = () => {
      const t = localStorage.getItem("pgf_token");
      if (t) return resolve(t);
      if (Date.now() - start >= maxMs) return resolve(null);
      setTimeout(check, intervalMs);
    };
    check();
  });
}

export default function CommissionerSetup() {
  useAuth(); // ensures auth context is mounted
  const nav = useNavigate();

  // Restore from localStorage on mount.
  const [form, setForm] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return { ...DEFAULT_FORM, ...JSON.parse(saved) };
    } catch {
      // ignore corrupt storage
    }
    return DEFAULT_FORM;
  });
  const [activeStep, setActiveStep] = useState(1);
  const [visitedSteps, setVisitedSteps] = useState(new Set([1]));
  const [leagueId, setLeagueId] = useState(null); // set after Launch
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  // Persist form to localStorage on every change (pre-launch).
  useEffect(() => {
    if (leagueId) return; // post-launch saves go through PATCH instead
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(form));
    } catch {
      // storage unavailable - non-fatal
    }
  }, [form, leagueId]);

  const updateForm = (patch) =>
    setForm((prev) => ({ ...prev, ...patch }));

  // [Phase 2c-D §6.2.1 R4] Full-replacement setter for Copy Settings - bypasses
  // the shallow-merge behavior of updateForm to avoid stale-state bleed when
  // copying from a prior league.
  const replaceForm = (newForm) =>
    setForm(() => ({ ...DEFAULT_FORM, ...newForm }));

  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const onCopySelect = (priorLeague) => {
    replaceForm(parseCopiedSettings(priorLeague));
    setCopyModalOpen(false);
    // Reset to Step 1 so the user reviews the league name + team name first.
    setActiveStep(1);
    setVisitedSteps(new Set([1]));
  };

  const stepValidation = useMemo(
    () => ({
      1: validateStep1(form),
      2: validateStep2(form),
      3: validateStep3(form),
      4: validateStep4(form),
    }),
    [form],
  );
  const requiredStepsValid =
    !stepValidation[1] && !stepValidation[2] && !stepValidation[4];
  // [2b-C redline] Commissioners must see budget + anti-snipe before committing.
  // Require Steps 2 and 4 to be visited in addition to being valid.
  const requiredStepsVisited =
    visitedSteps.has(2) && visitedSteps.has(4);
  const canLaunch = requiredStepsValid && requiredStepsVisited;

  const goToStep = (id) => {
    setError("");
    if (visitedSteps.has(id)) {
      setActiveStep(id);
      return;
    }
    // Linear-to-free: only advance to the next un-visited step if everything
    // before it is visited.
    const allPriorVisited = [...Array(id - 1)].every((_, i) =>
      visitedSteps.has(i + 1),
    );
    if (allPriorVisited) {
      setActiveStep(id);
      setVisitedSteps((prev) => new Set([...prev, id]));
    }
  };

  const onNext = () => {
    setError("");
    const stepErr = stepValidation[activeStep];
    if (stepErr) {
      setError(stepErr);
      return;
    }
    // Find the next sidebar step that exists.
    const next = STEPS.find((s) => s.id > activeStep);
    if (next) {
      setActiveStep(next.id);
      setVisitedSteps((prev) => new Set([...prev, next.id]));
    }
  };

  const onBack = () => {
    setError("");
    const prev = [...STEPS].reverse().find((s) => s.id < activeStep);
    if (prev) setActiveStep(prev.id);
  };

  const buildSettings = () => {
    // [§5.2] Emit scoring_rules only as user overrides; detect preset match.
    const overrides = pruneToOverrides(form.scoring_rules);
    const preset = detectPreset({ ...form.scoring_rules });
    // If all values match a preset exactly, keep scoring_format at that preset
    // name and send scoring_rules as empty {} (backend treats empty as "use preset").
    // Any deviation → scoring_format="custom" + emit only the deltas.
    const isCustom = Object.keys(overrides).length > 0 && preset === null;
    const scoring_format = isCustom ? "custom" : (preset || "half_ppr");
    const isSnake = form.draftFormat === "snake";
    // [Phase 2c-D §6.1] Snake forces use_keepers=false (until Phase 2d+).
    const use_keepers = isSnake ? false : !!form.use_keepers;
    return {
      league_type: computeLeagueType(form),
      // [Phase 2c-D §6.1] Persist draft_format so DraftLobby / Dashboard can
      // dispatch to /draft-snake/ vs /draft/.
      draft_format: form.draftFormat,
      num_teams: form.num_teams,
      // [Item 1] Season scoring shape. Only meaningful for season leagues;
      // the season build reads this when H2H matchups vs points standings
      // lands. Persisted now so setup is the single source of truth.
      season_league_type: form.top === "season" ? form.seasonLeagueType : null,
      budget: form.budget,
      roster: form.roster,
      roster_size: Object.values(form.roster).reduce((a, b) => a + b, 0),
      // Auction-only timers (no-ops for snake at the engine level, but
      // remain in settings for forward compatibility).
      bidding_timer_seconds: form.bidding_timer_seconds,
      nomination_timer_seconds: form.nomination_timer_seconds,
      antisnipe_trigger_seconds: form.antisnipe_trigger_seconds,
      antisnipe_extend_seconds: form.antisnipe_extend_seconds,
      // Snake-only timers (forward-compatible - engine reads these directly).
      // `pick_timer_enabled: false` = untimed draft; the engine then starts no
      // clock and never auto-picks, and pick_timer_seconds is ignored.
      pick_timer_enabled: form.pick_timer_enabled !== false,
      pick_timer_seconds: form.pick_timer_seconds,
      allow_pick_resubmit: !!form.allow_pick_resubmit,
      scoring_format,
      scoring_rules: overrides,
      use_keepers,
      nomination_order_mode: form.nomination_order_mode,
      // [Phase 2c-D §6.7 NEW-6] commissioner's own team name (optional).
      commissioner_team_name: (form.commissioner_team_name || "").trim() || null,
    };
  };

  const onLaunch = async () => {
    if (!canLaunch) {
      setError("Visit Steps 1, 2 and 4 and ensure they're valid before launch.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const token = await waitForToken(3000);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // [Phase 2d Stripe - Session B, founder-locked redlines] Auction gate.
      // Commissioners need has_auction_access + remaining capacity in the
      // 10-league quota OR an extended pass for unlimited leagues. Snake
      // leagues are free - bypass the check entirely.
      if (form.draftFormat === "auction") {
        try {
          const { data: ent } = await axios.get(`${API}/stripe/entitlements`, { headers });
          const hasAccess = !!ent?.has_auction_access;
          const extended = !!ent?.auction_extended;
          const used = ent?.auction_leagues_used || 0;
          const cap = ent?.auction_league_cap; // null when extended

          // Case 1: no auction pass yet → buy base pass.
          if (!hasAccess) {
            const { data: chk } = await axios.post(
              `${API}/stripe/create-checkout-session`,
              { product: "auction_draft_pass", origin_url: window.location.origin, league_id: "pending" },
              { headers },
            );
            window.location.href = chk.url;
            return;
          }
          // Case 2: at the 10-league cap → upgrade to extended.
          if (!extended && cap !== null && used >= cap) {
            const { data: chk } = await axios.post(
              `${API}/stripe/create-checkout-session`,
              { product: "auction_draft_pass_extended", origin_url: window.location.origin, league_id: "pending" },
              { headers },
            );
            window.location.href = chk.url;
            return;
          }
          // Case 3: under cap or already extended → proceed.
        } catch (gateErr) {
          // Fail-open on entitlement lookup error so paid users aren't blocked.
          console.warn("Auction pass gate check failed (continuing):", gateErr);
        }
      }

      const { data } = await axios.post(
        `${API}/leagues`,
        { name: form.name, settings: buildSettings() },
        { headers },
      );
      setLeagueId(data.id);
      // Pre-launch wizard state can be cleared post-launch.
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
      // Per league_type, redirect:
      const lt = data.settings?.league_type || computeLeagueType(form);
      if (lt === "LIVE_DRAFT_LIVE_SEASON") {
        nav(`/league/${data.id}/lobby`);
      } else if (lt.startsWith("MOCK_DRAFT_")) {
        nav(`/league/${data.id}/lobby`);
      } else if (lt === "NO_DRAFT_MOCK_SEASON") {
        // [Phase 2c-A bonus / Phase 1.5 hole patch]
        // NO_DRAFT_MOCK_SEASON: bootstrap CPU-fill + auto-roster + bridge,
        // then route directly into the season home (SeasonShell). Bridge
        // is idempotent - safe even if the user rapid-clicks Launch twice.
        try {
          await axios.post(
            `${API}/leagues/${data.id}/bootstrap-no-draft`,
            {},
            { headers },
          );
          nav(`/league/${data.id}/home`);
        } catch (bootstrapErr) {
          setError(
            bootstrapErr?.response?.data?.detail ||
              "Could not bootstrap mock season. League is created - try opening it from the dashboard.",
          );
          // Even on bootstrap failure the league exists; let the user retry
          // from dashboard rather than stranding them on the wizard.
          nav(`/dashboard`);
        }
      } else {
        // NO_DRAFT_LIVE_SEASON is grayed in Step 1 today - falls here only
        // if a future version ungrays it. Default to dashboard.
        nav(`/dashboard`);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create league");
    } finally {
      setBusy(false);
    }
  };

  const onSaveChanges = async () => {
    if (!leagueId) return;
    setBusy(true);
    setError("");
    try {
      await axios.put(
        `${API}/leagues/${leagueId}/settings`,
        { settings: buildSettings() },
        { headers: authHeaders() },
      );
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save changes");
    } finally {
      setBusy(false);
    }
  };

  const renderActive = () => {
    const isSnake = form.draftFormat === "snake";
    const isMockSeasonOnly = computeLeagueType(form) === "NO_DRAFT_MOCK_SEASON";
    switch (activeStep) {
      case 1:
        return (
          <Step1LeagueType
            form={form}
            update={updateForm}
            onOpenCopyModal={() => setCopyModalOpen(true)}
          />
        );
      case 2:
        return <Step2Roster form={form} update={updateForm} />;
      case 4:
        // [Phase 2c-D §6.7 NEW-7] NO_DRAFT_MOCK_SEASON has no draft, so Step 4
        // is meaningless. Render a short notice instead.
        if (isMockSeasonOnly) {
          return (
            <div data-testid="wizard-step-4-skipped" className="space-y-4 p-6 rounded-xl border border-[#252525] bg-[#1C1C1C]/40">
              <h2 className="font-display font-black text-2xl uppercase text-[#F5F5F5]">
                Draft Settings - Not Applicable
              </h2>
              <p className="font-body text-sm text-[#A0A0A0]">
                You picked "Season" + "Mock" - there's no draft, so no draft timers or budget to configure. Continue to the next step.
              </p>
            </div>
          );
        }
        return <Step4DraftSettings form={form} update={updateForm} isSnake={isSnake} />;
      case 3:
        return <Step3Scoring form={form} update={updateForm} />;
      case 5:
        return (
          <Step5SeasonTicketHolders
            form={{ ...form, _league_type: computeLeagueType(form) }}
            leagueId={leagueId}
            league={null}
          />
        );
      case 6:
        return <Step6Keepers form={form} update={updateForm} isSnake={isSnake} />;
      case 7:
        return <Step7NominationOrder form={form} update={updateForm} isSnake={isSnake} />;
      case 8:
        return (
          <Step8Review
            form={form}
            goToStep={goToStep}
            canLaunch={canLaunch}
            busy={busy}
            onLaunch={onLaunch}
          />
        );
      default:
        return null;
    }
  };

  const stepStatus = (id) => {
    if (id === activeStep) return "active";
    const err = stepValidation[id];
    if (visitedSteps.has(id) && !err) return "valid";
    if (visitedSteps.has(id) && err) return "invalid";
    return "locked";
  };

  return (
    <div data-testid="wizard-shell" className="min-h-screen bg-[#111111] text-[#F5F5F5] flex flex-col">
      <CopySettingsModal
        isOpen={copyModalOpen}
        onClose={() => setCopyModalOpen(false)}
        onSelect={onCopySelect}
      />
      {/* Header */}
      <header className="bg-[#1C1C1C] border-b border-[#252525] h-16 flex items-center px-4 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto w-full flex items-center gap-3">
          <button
            data-testid="wizard-back-to-dashboard"
            onClick={() => nav("/dashboard")}
            className="text-[#A0A0A0] hover:text-[#FF6B00] transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-2">
            <div className="bg-[#FF6B00] px-2 py-0.5">
              <span className="font-display font-black text-white text-sm uppercase tracking-wider">
                PGF
              </span>
            </div>
            <span className="font-display font-black uppercase text-base">
              {leagueId ? "Edit League" : "Create League"}
            </span>
          </div>
          {savedFlash && (
            <span className="ml-auto text-[#00C853] font-body text-sm flex items-center gap-1">
              <Check size={14} /> Saved
            </span>
          )}
        </div>
      </header>

      {/* Body: sidebar + content */}
      <div className="flex-1 max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 px-4 py-8">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 self-start">
          <nav className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-2">
            <ul className="space-y-1">
              {STEPS.map((s) => {
                const status = stepStatus(s.id);
                const dotClass =
                  status === "active"
                    ? "bg-[#FF6B00] text-white"
                    : status === "valid"
                    ? "bg-[#00C853] text-[#111111]"
                    : status === "invalid"
                    ? "bg-[#FF4444]/20 text-[#FF4444] border border-[#FF4444]"
                    : "bg-[#252525] text-[#A0A0A0]";
                return (
                  <li key={s.id}>
                    <button
                      type="button"
                      data-testid={`wizard-sidebar-step-${s.id}`}
                      onClick={() => goToStep(s.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        status === "active"
                          ? "bg-[#FF6B00]/10"
                          : "hover:bg-[#252525]/50"
                      }`}
                    >
                      <span
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-display font-black shrink-0 ${dotClass}`}
                      >
                        {status === "valid" ? <Check size={12} /> : s.id}
                      </span>
                      <span className="flex-1">
                        <span
                          className={`block font-display font-bold text-sm uppercase tracking-wide ${
                            status === "active"
                              ? "text-[#FF6B00]"
                              : "text-[#F5F5F5]"
                          }`}
                        >
                          {s.label}
                        </span>
                        <span className="block font-body text-[10px] text-[#A0A0A0]">
                          {s.phase}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>

        {/* Content */}
        <section className="min-w-0">
          {error && (
            <div
              data-testid="wizard-error"
              className="bg-[#FF4444]/10 border border-[#FF4444]/30 rounded-lg px-4 py-3 text-[#FF4444] font-body text-sm mb-6"
            >
              {error}
            </div>
          )}

          {renderActive()}

          {/* Bottom action bar (R4: contextual label) */}
          <div className="flex items-center justify-between mt-12 pt-6 border-t border-[#252525]">
            <button
              data-testid="wizard-back-btn"
              type="button"
              onClick={onBack}
              disabled={activeStep === 1}
              className="flex items-center gap-2 font-body text-sm text-[#A0A0A0] hover:text-[#FF6B00] transition-colors disabled:opacity-30"
            >
              <ArrowLeft size={16} /> Back
            </button>

            <div className="flex items-center gap-3">
              {activeStep < 8 && (
                <button
                  data-testid="wizard-next-btn"
                  type="button"
                  onClick={onNext}
                  className="font-display font-bold text-sm uppercase px-4 py-2 rounded-lg bg-[#252525] hover:bg-[#333] text-[#F5F5F5] transition-colors"
                >
                  Next →
                </button>
              )}

              {/* Step 8 has its own inline Launch CTA - hide bottom-bar button there. */}
              {activeStep !== 8 && (leagueId ? (
                <button
                  data-testid="wizard-save-changes-btn"
                  type="button"
                  onClick={onSaveChanges}
                  disabled={busy}
                  className="flex items-center gap-2 bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-50 text-white font-display font-black uppercase px-6 py-3 rounded-lg transition-all active:scale-95"
                >
                  <Save size={16} /> Save Changes
                </button>
              ) : (
                <button
                  data-testid="wizard-launch-btn"
                  type="button"
                  onClick={onLaunch}
                  disabled={busy || !canLaunch}
                  className="flex items-center gap-2 bg-[#FF6B00] hover:bg-[#FF8533] disabled:opacity-50 disabled:cursor-not-allowed text-white font-display font-black uppercase px-6 py-3 rounded-lg transition-all active:scale-95"
                >
                  <Rocket size={16} />
                  {busy ? "Creating..." : "Launch League"}
                </button>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
